import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { adminApi, errText, fmtDate } from "@/lib/api";

const StatCard = ({ label, value, sub, testId }) => (
  <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid={testId}>
    <p className="text-xs uppercase tracking-wider text-[#71717A] font-bold">{label}</p>
    <p className="text-3xl font-extrabold mt-1.5 text-[#0A0A0A]">{value}</p>
    {sub && <p className="text-xs text-[#71717A] mt-1">{sub}</p>}
  </div>
);

const tooltipStyle = {
  backgroundColor: "#FFFFFF", border: "1px solid #E4E4E7", borderRadius: 4, color: "#0A0A0A", fontSize: 12,
};

export function DashboardTab() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/dashboard")
      .then(({ data }) => setStats(data))
      .catch((e) => toast.error(errText(e)));
    adminApi.get("/admin/analytics")
      .then(({ data }) => setAnalytics(data))
      .catch(() => { /* silent — analytics is supplementary */ });
  }, []);

  if (!stats) return <p className="mt-8 text-[#71717A] text-sm">Loading dashboard…</p>;

  const maxFunnel = Math.max(...stats.funnel.map((f) => f.count), 1);

  return (
    <div className="mt-5 space-y-5" data-testid="dashboard-content">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testId="stat-today-leads" label="Today's Leads" value={stats.today_leads} />
        <StatCard testId="stat-total-leads" label="Total Leads" value={stats.total_leads} />
        <StatCard testId="stat-otp-rate" label="OTP Verification" value={`${stats.otp_rate}%`} sub={`${stats.otp_verified}/${stats.otp_sent} verified`} />
        <StatCard testId="stat-redemption-rate" label="Redemption Rate" value={`${stats.redemption_rate}%`} sub={`${stats.redeemed_rewards}/${stats.total_rewards} coupons used`} />
      </div>

      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-kpi-strip">
          <StatCard testId="stat-repeat-rate" label="Repeat Visitor Rate" value={`${analytics.otp_repeat_rate}%`}
            sub={`${analytics.repeat_otp_phones}/${analytics.total_otp_phones} phones returned`} />
          <StatCard testId="stat-visits-claim" label="Visits per Claim" value={analytics.visits_to_claim_ratio}
            sub={`${analytics.total_otp_requests} OTPs · ${analytics.visits_to_reward_ratio} per reward`} />
          <StatCard testId="stat-care-plus-conv" label="Care+ Conversion" value={`${analytics.care_plus_conversion_rate}%`}
            sub={`${analytics.paid_customers}/${analytics.total_customers} customers`} />
          <StatCard testId="stat-revenue" label="Care+ Revenue" value={`₹${analytics.total_revenue_rupees.toLocaleString("en-IN")}`}
            sub={`${analytics.returning_customers} returning · ${analytics.returning_rate}%`} />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
          <p className="text-sm font-bold mb-4 text-[#0A0A0A]">Leads by Source</p>
          {stats.by_source.length === 0 ? (
            <p className="text-xs text-[#71717A]">No data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.by_source}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="source" tick={{ fill: "#71717A", fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fill: "#71717A", fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,90,0,0.08)" }} />
                <Bar dataKey="count" fill="#FF5A00" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
          <p className="text-sm font-bold mb-4 text-[#0A0A0A]">Prize Distribution</p>
          {stats.prize_distribution.length === 0 ? (
            <p className="text-xs text-[#71717A]">No rewards issued yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.prize_distribution} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis type="number" allowDecimals={false} tick={{ fill: "#71717A", fontSize: 11 }} />
                <YAxis type="category" dataKey="prize" width={140} tick={{ fill: "#52525B", fontSize: 10 }} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,90,0,0.08)" }} />
                <Bar dataKey="count" fill="#FF5A00" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="conversion-funnel">
          <p className="text-sm font-bold mb-4 text-[#0A0A0A]">Conversion Funnel</p>
          <div className="space-y-3">
            {stats.funnel.map((f) => (
              <div key={f.stage}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[#52525B] font-semibold">{f.stage}</span>
                  <span className="font-bold text-[#0A0A0A]">{f.count}</span>
                </div>
                <div className="h-2.5 bg-[#F4F4F5] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#FF5A00] rounded-full transition-all"
                    style={{ width: `${(f.count / maxFunnel) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#D4AF37]/50 rounded-sm p-5" data-testid="expiring-codes">
          <p className="text-sm font-bold mb-4 text-[#0A0A0A]">⏰ Unredeemed Codes Expiring Within 3 Days</p>
          {stats.expiring_soon.length === 0 ? (
            <p className="text-xs text-[#71717A]">Nothing expiring soon. All clear!</p>
          ) : (
            <div className="space-y-2 max-h-[220px] overflow-y-auto">
              {stats.expiring_soon.map((r) => (
                <div key={r.code} className="flex items-center justify-between bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm px-3.5 py-2.5 text-xs">
                  <div>
                    <p className="font-bold text-[#0A0A0A]">{r.name} · {r.phone}</p>
                    <p className="text-[#71717A]">{r.prize}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-bold text-[#FF5A00]">{r.code}</p>
                    <p className="text-[#D4AF37]">expires {fmtDate(r.expires_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
